/*
 * Copyright (c) 2019 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, Inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params } from '@angular/router';
import { UserModel } from '../user.model';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ROLE_SERVICE_TOKEN } from '../../roles/roles.config';
import { USERS_SAVE_SERVICE_TOKEN } from '../../users.config';
import { RoleWithRightsVo } from '../../roles/models/role-with-rights-vo.model';
import { AbstractSaveUserService } from '../../abstract-save-user.service';
import { AbstractRoleService } from '../../roles/abstract-role.service';

@Component({
  selector: 'auth-user-details',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent implements OnInit {
  id: string | undefined = undefined;
  userData: UserModel | undefined = undefined;
  roleWithRightsVOs: RoleWithRightsVo[] = [];
  emailValue: string | null = null;
  statusMessages = [];

  private owner: string = null;

  constructor(
    private readonly _location: Location,
    private readonly route: ActivatedRoute,
    @Inject(USERS_SAVE_SERVICE_TOKEN)
    private readonly usersService: AbstractSaveUserService,
    @Inject(ROLE_SERVICE_TOKEN)
    private readonly roleService: AbstractRoleService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(
        switchMap((data: Params) => {
          return forkJoin([
            this.getUserDetails$(data),
            this.getUserRoles$(data)
          ]);
        })
      )
      .subscribe((res: [UserModel, RoleWithRightsVo[]]) => {
        this.userData = res[0];
        this.emailValue = res[0].email;
        this.roleWithRightsVOs = res[1];
      });

    this.route.queryParams.subscribe((data) => {
      if (data && data['legalEntity']) {
        this.owner = data['legalEntity'];
      }
    });
  }

  getUserDetails$(data: any): Observable<UserModel> {
    if (data && data.uuid && data.uuid !== '') {
      this.id = data.uuid;
      return this.usersService.getUserById(this.id);
    } else {
      return this.usersService.getNewUser();
    }
  }

  getUserRoles$(data: any): Observable<RoleWithRightsVo[]> {
    if (data && data.uuid && data.uuid !== '') {
      this.id = data.uuid;
      return this.roleService
        .findRolesByUserUuid(this.id)
        .pipe(map((res) => res.data));
    } else {
      return this.roleService
        .findRolesForNewUser()
        .pipe(map((res) => res.data));
    }
  }

  goBack() {
    this._location.back();
  }

  onUserSubmit(formValue: any) {
    if (this.owner) {
      formValue['owner'] = this.owner;
    }
    formValue['activated'] = this.userData?.activated;
    delete formValue['confirmEmail'];
    this.usersService.saveUserRights(formValue).subscribe(
      () => {
        this.goBack();
      },
      (res) => {
        this.translateService
          .stream('auth.errors.oldPassword.' + res.error.message)
          .subscribe((resMessage: string) => {
            this.statusMessages = [];
            this.statusMessages.push({
              severity: 'error',
              summary: resMessage,
            });
          });
      }
    );
  }
}
